// client/components/evaluate/DecisionDialog.tsx
import React, { useEffect, useState } from 'react';
import type { FairRuleResult } from '../../hooks/useEvaluateData';

export type DecisionAction = 'accept' | 'reject' | 'edit';

export interface DecisionDialogResult {
  rule_id: string;
  action: DecisionAction;
  asserted_status: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
  rationale: string;
  scope_type: 'dataset' | 'catalog' | 'schema' | 'global';
  scope_natural_key: string | null;
  machine_verdict_at_decision_time: 'PASS' | 'FAIL' | 'NOT_APPLICABLE';
  /**
   * Tier-bearing rules only (F2 today). 0 = below Tier 1; 1, 2, 3 = explicit
   * tier. Null for binary-status decisions.
   */
  asserted_maturity_tier: number | null;
  machine_maturity_tier_at_decision_time: number | null;
}

interface DecisionDialogProps {
  rule: FairRuleResult;
  initialAction: DecisionAction;
  datasetNaturalKey: string | null;
  schemaNaturalKey: string | null;
  catalogNaturalKey: string | null;
  onSubmit: (result: DecisionDialogResult) => Promise<void>;
  onCancel: () => void;
}

type ScopeType = 'dataset' | 'catalog' | 'schema' | 'global';
type AssertedStatus = 'PASS' | 'FAIL' | 'NOT_APPLICABLE';

function defaultAsserted(
  action: DecisionAction,
  status: AssertedStatus,
): AssertedStatus {
  if (action === 'accept') return status;
  if (action === 'reject') {
    if (status === 'PASS') return 'FAIL';
    if (status === 'FAIL') return 'PASS';
    return status;
  }
  return status;
}

export function DecisionDialog({
  rule,
  initialAction,
  datasetNaturalKey,
  schemaNaturalKey,
  catalogNaturalKey,
  onSubmit,
  onCancel,
}: DecisionDialogProps) {
  const [action, setAction] = useState<DecisionAction>(initialAction);
  const [rationale, setRationale] = useState('');
  const [assertedStatus, setAssertedStatus] = useState<AssertedStatus>(
    defaultAsserted(initialAction, rule.status),
  );
  const [scopeType, setScopeType] = useState<ScopeType>('dataset');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tier-bearing rules (F2 today; F1/R1 in v1.3+) carry a maturity_breakdown.
  // The dialog exposes a tier picker for these rules. Status is derived from
  // tier (≥1 → PASS, 0 → FAIL) so reviewers think in rubric terms, not
  // PASS/FAIL collapse.
  const breakdown = rule.evidence_structured.maturity_breakdown;
  const isTiered = breakdown !== undefined;
  const machineTier = breakdown?.tier_achieved ?? null;
  const tierMax = breakdown?.tier_max ?? 3;
  const [assertedTier, setAssertedTier] = useState<number>(machineTier ?? 0);

  useEffect(() => {
    setAssertedStatus(defaultAsserted(action, rule.status));
  }, [action, rule.status]);

  // For tiered rules, when the action changes default the tier picker:
  //   - accept → machine's tier
  //   - reject → 0 (below Tier 1, the canonical Reject downgrade)
  //   - edit   → keep last picker state (reviewer is choosing)
  useEffect(() => {
    if (!isTiered) return;
    if (action === 'accept') setAssertedTier(machineTier ?? 0);
    else if (action === 'reject') setAssertedTier(0);
  }, [action, isTiered, machineTier]);

  // For tiered rules, asserted_status is derived from the tier — don't let it
  // drift independently. Tier ≥ 1 → PASS, Tier 0 → FAIL.
  useEffect(() => {
    if (!isTiered) return;
    setAssertedStatus(assertedTier >= 1 ? 'PASS' : 'FAIL');
  }, [assertedTier, isTiered]);

  const scopeNaturalKeyForType: Record<ScopeType, string | null> = {
    dataset: datasetNaturalKey,
    schema: schemaNaturalKey,
    catalog: catalogNaturalKey,
    global: null,
  };
  const resolvedNaturalKey = scopeNaturalKeyForType[scopeType];

  const scopeAvailable: Record<ScopeType, boolean> = {
    dataset: datasetNaturalKey !== null,
    schema: schemaNaturalKey !== null,
    catalog: catalogNaturalKey !== null,
    global: true,
  };

  const canSubmit = scopeType === 'global' || resolvedNaturalKey !== null;

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        rule_id: rule.rule,
        action,
        asserted_status: assertedStatus,
        rationale: rationale.trim(),
        scope_type: scopeType,
        scope_natural_key: resolvedNaturalKey,
        machine_verdict_at_decision_time:
        rule.evidence_structured.machine_verdict_preserved?.status ?? rule.status,
        asserted_maturity_tier: isTiered ? assertedTier : null,
        machine_maturity_tier_at_decision_time: isTiered ? machineTier : null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel = action === 'accept' ? 'Accept' : action === 'reject' ? 'Reject' : 'Edit';

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--tri-card)', border: '1px solid var(--tri-taupe)',
          borderRadius: 10, padding: 24, width: 520, color: 'var(--tri-ink)',
          maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <h2 style={{ marginTop: 0, fontSize: '1.1rem' }}>
          {actionLabel} {rule.rule} · {rule.ruleName}
        </h2>

        <Field label="Action">
          <RadioGroup
            name="decision-action"
            value={action}
            options={[
              { value: 'accept', label: 'Accept (concur with machine)' },
              { value: 'reject', label: 'Reject (flip the verdict)' },
              { value: 'edit', label: 'Edit (assert your own verdict + narrative)' },
            ]}
            onChange={(v) => setAction(v as DecisionAction)}
          />
        </Field>

        {isTiered ? (
          <Field label="Asserted maturity tier">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <RadioGroup
                name="decision-tier"
                value={String(assertedTier)}
                options={[
                  { value: '0', label: '0 — Below Tier 1' },
                  { value: '1', label: '1 — Minimal' },
                  { value: '2', label: '2 — Citation' },
                  { value: '3', label: '3 — Descriptive' },
                ].slice(0, Math.max(2, tierMax + 1))}
                onChange={(v) => setAssertedTier(parseInt(v, 10))}
                disabled={action === 'accept'}
              />
              {action === 'accept' && (
                <span style={{ color: 'var(--tri-slate)', fontSize: '0.8rem' }}>
                  (locked to machine tier {machineTier ?? 0} of {tierMax} for Accept)
                </span>
              )}
            </div>
            <div style={{ marginTop: 6, fontSize: '0.72rem', color: 'var(--tri-slate)' }}>
              Status will be recorded as <span style={{ color: assertedTier >= 1 ? 'var(--tri-good)' : 'var(--tri-hard)' }}>
                {assertedTier >= 1 ? 'PASS' : 'FAIL'}
              </span> · machine reported Tier {machineTier ?? 0} of {tierMax}.
            </div>
          </Field>
        ) : (
          <Field label="Asserted status">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <RadioGroup
                name="decision-status"
                value={assertedStatus}
                options={[
                  { value: 'PASS', label: 'PASS' },
                  { value: 'FAIL', label: 'FAIL' },
                  { value: 'NOT_APPLICABLE', label: 'N/A' },
                ]}
                onChange={(v) => setAssertedStatus(v as AssertedStatus)}
                disabled={action === 'accept'}
              />
              {action === 'accept' && (
                <span style={{ color: 'var(--tri-slate)', fontSize: '0.8rem' }}>
                  (locked to machine verdict for Accept)
                </span>
              )}
            </div>
          </Field>
        )}

        <Field label="Apply at scope">
          <select
            value={scopeType}
            onChange={(e) => setScopeType(e.target.value as ScopeType)}
            style={{
              padding: '6px 10px', borderRadius: 6, background: 'var(--tri-card-2)',
              color: 'var(--tri-ink)', border: '1px solid var(--tri-taupe)', minWidth: 280,
            }}
          >
            {scopeAvailable.dataset && (
              <option value="dataset">This dataset · {datasetNaturalKey}</option>
            )}
            {scopeAvailable.schema && (
              <option value="schema">Schema · {schemaNaturalKey}</option>
            )}
            {scopeAvailable.catalog && (
              <option value="catalog">Catalog · {catalogNaturalKey}</option>
            )}
            <option value="global">Global (every dataset)</option>
          </select>
          {!scopeAvailable[scopeType] && (
            <div style={{ marginTop: 4, color: 'var(--tri-hard)', fontSize: '0.75rem' }}>
              This scope is not available for this dataset.
            </div>
          )}
        </Field>

        <Field label={`Rationale ${action === 'accept' ? '(optional)' : ''}`}>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder={
              action === 'accept'
                ? 'Why this rule passes/fails as machine reported it.'
                : 'Why you disagree with the machine verdict.'
            }
            style={{
              width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 6,
              background: 'var(--tri-card-2)', color: 'var(--tri-ink)',
              border: '1px solid var(--tri-taupe)', fontFamily: 'inherit',
              fontSize: '0.85rem', resize: 'vertical',
            }}
          />
        </Field>

        {error && (
          <div style={{ marginTop: 8, color: 'var(--tri-hard)', fontSize: '0.8rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: '6px 14px', borderRadius: 6, background: 'transparent',
              color: 'var(--tri-slate)', border: '1px solid var(--tri-taupe)', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !canSubmit}
            style={{
              padding: '6px 14px', borderRadius: 6,
              background: 'var(--tri-good-bg)', color: 'var(--tri-good)',
              border: '1px solid var(--tri-good)',
              cursor: submitting || !canSubmit ? 'not-allowed' : 'pointer',
              opacity: submitting || !canSubmit ? 0.5 : 1,
            }}
          >
            {submitting ? 'Saving…' : 'Submit decision'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{
        fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em',
        color: 'var(--tri-slate)', marginBottom: 4,
      }}>
        {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

interface RadioGroupProps {
  name: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
  disabled?: boolean;
}

function RadioGroup({ name, value, options, onChange, disabled }: RadioGroupProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {options.map((opt) => (
        <label
          key={opt.value}
          style={{
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? 'var(--tri-slate)' : 'var(--tri-ink)',
            fontSize: '0.85rem',
          }}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            disabled={disabled}
            onChange={() => onChange(opt.value)}
            style={{ marginRight: 6 }}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}
